import { createFileRoute, Link } from "@tanstack/react-router";
import { withMemberGate } from "@/lib/with-member-gate";
import { useSleepStore } from "@/lib/use-sleep-store";
import { accentAt } from "@/lib/journal-accents";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import {
  Moon,
  Sun,
  Star,
  Stars,
  Sparkles,
  BookOpen,
  Headphones,
  Waves,
  Cloud,
  CloudMoon,
  Heart,
  TrendingUp,
  Plus,
  Trash2,
  CalendarDays,
  Wind,
  Activity,
  Shield,
  Play,
  Pause,
  RotateCcw,
  X,
  ChevronDown,
  PenLine,
  MoonStar,
  Brain,
  Eye,
  WandSparkles,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { DREAM_EMOTIONS, DREAM_SYMBOLS, type DreamVisualOption } from "@/lib/dream-options";
import { FavoriteButton } from "@/components/FavoriteButton";

export const Route = createFileRoute("/sleep-dreams")({
  head: () => ({
    meta: [
      { name: "robots", content: "noindex" },
      { title: "Sleep & Dreams — Recovery Trauma™" },
      {
        name: "description",
        content:
          "A trauma-informed sanctuary to track sleep, journal dreams, and explore what arises during the night.",
      },
      { property: "og:title", content: "Sleep & Dreams — Recovery Trauma™" },
      { property: "og:description", content: "A trauma-informed sanctuary to track sleep, journal dreams, and explore what arises during the night." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:url", content: "https://recoverytrauma.com/sleep-dreams" },
    ],
    links: [{ rel: "canonical", href: "https://recoverytrauma.com/sleep-dreams" }],
  }),
  component: withMemberGate(SleepDreamsPage, "Sleep & Dreams"),
});

// ---------------- Types & storage ----------------

type SleepEntry = {
  id: string;
  date: string; // ISO yyyy-mm-dd
  bedtime: string; // HH:MM
  wakeTime: string;
  hours: number;
  quality: number; // 1-10
  energy: number; // 1-10
  awakenings: number;
  notes: string;
};

type DreamEntry = {
  id: string;
  date: string;
  title: string;
  description: string;
  emotions: string[];
  people: string;
  locations: string;
  symbols: string[];
  themes: string[];
  reflection: string;
  reflections?: Array<{
    id: string;
    title: string;
    body: string;
    createdAt: string;
    updatedAt: string;
  }>;
};

type CheckIn = {
  id: string;
  date: string;
  rested: number;
  connected: number;
  rememberedDream: boolean;
  bodyNeeds: string;
};


const todayISO = () => new Date().toISOString().slice(0, 10);
const uid = () =>
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : Math.random().toString(36).slice(2);

// ---------------- Static content ----------------

const DREAM_THEMES = [
  "Safety",
  "Fear",
  "Relationships",
  "Childhood Memories",
  "Personal Growth",
  "Loss",
  "Healing",
  "Transformation",
  "Boundaries",
  "Self-Worth",
];

const DREAM_REFLECTION_PROMPTS = [
  "What emotion felt strongest?",
  "What stands out most now?",
  "Did any part feel familiar?",
  "What did you notice in your body on waking?",
  "Does anything connect with your waking life?",
  "What feels important or in need of care today?",
];

const DREAM_GUIDES = [
  {
    id: "why",
    icon: Brain,
    title: "Why do we dream?",
    summary: "What research suggests — without treating dreams as diagnoses.",
    content: (
      <>
        There is no single proven reason. Dreaming may help the brain organise memories, rehearse
        experiences and process emotion during sleep. Dreams can also be fragmented or random.
        Their meaning is personal; they are not predictions or proof of a diagnosis.
      </>
    ),
  },
  {
    id: "recall",
    icon: BookOpen,
    title: "How can I remember dreams?",
    summary: "Small morning habits can make recall easier over time.",
    content: (
      <ul className="space-y-2">
        <li>Stay still for a moment when you wake and notice the last image, feeling or place.</li>
        <li>Ask, “What was happening just before I woke?” without forcing an answer.</li>
        <li>Write or record a few words immediately — fragments count.</li>
        <li>Keep your journal close and revisit recurring people, places and feelings gently.</li>
      </ul>
    ),
  },
  {
    id: "lucid",
    icon: Eye,
    title: "What is lucid dreaming?",
    summary: "Becoming aware that you are dreaming while the dream continues.",
    content: (
      <>
        <p>
          Dream recall is a helpful first step. Before sleep, set a simple intention: “If I dream,
          I may notice that I am dreaming.” During the day, pause occasionally and gently ask whether
          you are awake while noticing your surroundings.
        </p>
        <p className="mt-2">
          Protect your sleep rather than repeatedly waking yourself. If lucid-dream practices increase
          nightmares, distress, confusion or disconnection, pause them and return to ordinary rest and grounding.
        </p>
      </>
    ),
  },
] as const;

const SLEEP_LIBRARY = [
  {
    icon: Moon,
    title: "Trauma and Sleep",
    blurb:
      "Why trauma disrupts sleep architecture, and the gentle steps that help the nervous system trust rest again.",
  },
  {
    icon: Cloud,
    title: "Nightmares and Recovery",
    blurb:
      "Understanding nightmares as the brain's processing — not a sign you are broken.",
  },
  {
    icon: Sparkles,
    title: "Understanding Dream Patterns",
    blurb:
      "How recurring symbols, people, and themes can mirror what your inner world is working through.",
  },
  {
    icon: Wind,
    title: "Nervous System Regulation Before Bed",
    blurb:
      "Simple practices to move from sympathetic activation into parasympathetic rest.",
  },
  {
    icon: Shield,
    title: "Sleep Hygiene for Trauma Survivors",
    blurb:
      "A gentle, choice-led approach that prioritizes safety over rigid routines.",
  },
  {
    icon: Activity,
    title: "Why Healing Can Affect Dreams",
    blurb:
      "When old material surfaces during deep work, dreams often become more vivid. Here's why that's okay.",
  },
];

type Phase = { label: string; seconds: number };
type Practice = {
  title: string;
  duration: string;
  blurb: string;
  icon: typeof Wind;
  intro: string;
  phases: Phase[];
};

const GUIDED_PRACTICES: Practice[] = [
  {
    title: "Bedtime Breathing",
    duration: "5 min",
    blurb: "Gentle breathing to prepare for sleep.",
    icon: Wind,
    intro: "We'll move through slow 4-7-8 breathing to invite the body toward rest.",
    phases: [
      { label: "Settle in. Soften the jaw and shoulders.", seconds: 20 },
      { label: "Breathe in slowly through the nose… 1, 2, 3, 4.", seconds: 4 },
      { label: "Hold gently… 1, 2, 3, 4, 5, 6, 7.", seconds: 7 },
      { label: "Exhale slowly through the mouth… 1, 2, 3, 4, 5, 6, 7, 8.", seconds: 8 },
      { label: "Inhale… 4 counts.", seconds: 4 },
      { label: "Hold… 7 counts.", seconds: 7 },
      { label: "Exhale… 8 counts.", seconds: 8 },
      { label: "Inhale… 4 counts.", seconds: 4 },
      { label: "Hold… 7 counts.", seconds: 7 },
      { label: "Exhale… 8 counts.", seconds: 8 },
      { label: "One more round. Inhale… 4.", seconds: 4 },
      { label: "Hold… 7.", seconds: 7 },
      { label: "Exhale… 8.", seconds: 8 },
      { label: "Let the breath return to its own rhythm.", seconds: 30 },
      { label: "Notice any softening. You can stay here as long as you like.", seconds: 30 },
      { label: "When you're ready, gently open your eyes.", seconds: 20 },
    ],
  },
  {
    title: "Evening Nervous System Reset",
    duration: "10 min",
    blurb: "Release the stress of the day.",
    icon: Activity,
    intro: "A slow sequence of grounding cues to discharge held tension from the day.",
    phases: [
      { label: "Sit or lie down. Feel the support beneath you.", seconds: 30 },
      { label: "Place one hand on your chest, one on your belly.", seconds: 30 },
      { label: "Take three long, slow breaths — exhale longer than the inhale.", seconds: 45 },
      { label: "Soften your eyes. Let the gaze fall.", seconds: 30 },
      { label: "Gently look to the right as far as is comfortable.", seconds: 30 },
      { label: "Now slowly to the left.", seconds: 30 },
      { label: "Return to center. Notice any sigh or yawn that wants to come.", seconds: 30 },
      { label: "Hum a low, steady tone on the exhale. Feel the vibration.", seconds: 45 },
      { label: "Again — hum on the exhale.", seconds: 45 },
      { label: "Press your feet gently into the floor or bed.", seconds: 30 },
      { label: "Squeeze and release your hands three times.", seconds: 30 },
      { label: "Bring your attention to anywhere in the body that feels neutral or okay.", seconds: 45 },
      { label: "Rest your awareness there. This is a place of safety.", seconds: 45 },
      { label: "Three more slow breaths.", seconds: 45 },
      { label: "Whenever you're ready, return to the room.", seconds: 30 },
    ],
  },
  {
    title: "Progressive Muscle Relaxation",
    duration: "8 min",
    blurb: "Reduce physical tension before bed.",
    icon: Heart,
    intro: "We'll gently tense and release muscle groups from feet to head.",
    phases: [
      { label: "Lie down comfortably. Let the body be heavy.", seconds: 30 },
      { label: "Curl your toes and tense your feet… hold.", seconds: 8 },
      { label: "Release. Notice the softening.", seconds: 20 },
      { label: "Tighten your calves and thighs… hold.", seconds: 8 },
      { label: "Release. Let the legs sink.", seconds: 20 },
      { label: "Squeeze your glutes and lower belly… hold.", seconds: 8 },
      { label: "Release. Feel the warmth.", seconds: 20 },
      { label: "Clench your hands into fists… hold.", seconds: 8 },
      { label: "Release. Let the fingers spread.", seconds: 20 },
      { label: "Tense your arms and shoulders… hold.", seconds: 8 },
      { label: "Release. Let them drop.", seconds: 25 },
      { label: "Scrunch your face — eyes, jaw, brow… hold.", seconds: 8 },
      { label: "Release. Soften everything.", seconds: 25 },
      { label: "Tense the whole body at once… hold.", seconds: 8 },
      { label: "Release everything. Sink in.", seconds: 30 },
      { label: "Scan from head to toe. Notice the heaviness.", seconds: 45 },
      { label: "Rest here in the softness. Breathe naturally.", seconds: 60 },
    ],
  },
  {
    title: "Guided Sleep Meditation",
    duration: "15 min",
    blurb: "Support calm and restful sleep.",
    icon: Moon,
    intro: "A slow drift downward into rest — gentle imagery and long pauses.",
    phases: [
      { label: "Get comfortable. Pull the covers close.", seconds: 30 },
      { label: "Let your breath move on its own. No effort.", seconds: 45 },
      { label: "Imagine soft golden light at the top of your head.", seconds: 45 },
      { label: "The light moves down through your face… softening everything.", seconds: 60 },
      { label: "Down through the throat and chest. Warm and slow.", seconds: 60 },
      { label: "Through the belly. Through the hips.", seconds: 60 },
      { label: "Down the legs… all the way to the feet.", seconds: 60 },
      { label: "Your whole body is held in this gentle light.", seconds: 60 },
      { label: "Picture yourself in a place that feels safe — real or imagined.", seconds: 60 },
      { label: "Notice the colors. The sounds. The temperature.", seconds: 60 },
      { label: "You are safe here. There is nothing to do.", seconds: 60 },
      { label: "If thoughts arise, let them drift like clouds.", seconds: 60 },
      { label: "Rest in this place. Breathe slowly.", seconds: 90 },
      { label: "Let yourself sink deeper. Sleep is welcome.", seconds: 90 },
      { label: "Stay as long as you like. You can drift off here.", seconds: 60 },
    ],
  },
  {
    title: "Safe Place Visualization",
    duration: "10 min",
    blurb: "Create a felt sense of safety before sleep.",
    icon: Shield,
    intro: "We'll build an inner sanctuary you can return to anytime.",
    phases: [
      { label: "Settle in. Take three slow breaths.", seconds: 30 },
      { label: "Picture a place where you feel completely safe.", seconds: 45 },
      { label: "It can be real, imagined, indoors or outdoors. Any place.", seconds: 30 },
      { label: "What do you see around you? Notice the details.", seconds: 60 },
      { label: "What sounds are here? Or is it quiet?", seconds: 45 },
      { label: "Is it warm or cool? Feel it on your skin.", seconds: 45 },
      { label: "What scents are in the air?", seconds: 30 },
      { label: "Notice what's beneath you — earth, water, soft fabric…", seconds: 45 },
      { label: "Let your body feel held by this place.", seconds: 60 },
      { label: "Place a hand where you feel safety most. Anchor it.", seconds: 45 },
      { label: "Know: this place is always here. You can return anytime.", seconds: 60 },
      { label: "Rest in the safety. Breathe slowly.", seconds: 60 },
      { label: "When you're ready, gently bring awareness back to the room.", seconds: 45 },
    ],
  },
];

const SOUNDSCAPES = [
  { title: "Soft Rainfall", duration: "60 min" },
  { title: "Ocean Waves", duration: "45 min" },
  { title: "Forest at Night", duration: "60 min" },
  { title: "Warm Fireside", duration: "30 min" },
  { title: "Gentle Wind", duration: "45 min" },
  { title: "Ambient Calm", duration: "90 min" },
];

const TABS = [
  { id: "tracker", label: "Sleep Tracker", icon: Moon },
  { id: "dreams", label: "Dream Journal", icon: Sparkles },
  { id: "checkin", label: "Daily Check-In", icon: Sun },
  { id: "patterns", label: "Patterns", icon: TrendingUp },
  { id: "library", label: "Sleep Library", icon: BookOpen },
  { id: "practices", label: "Guided Practices", icon: Headphones },
  { id: "sounds", label: "Soundscapes", icon: Waves },
] as const;
type TabId = (typeof TABS)[number]["id"];

// ---------------- Page ----------------

function SleepDreamsPage() {
  const [tab, setTab] = useState<TabId>("tracker");

  return (
    <div className="min-h-dvh bg-background">
      <div className="mx-auto max-w-5xl px-4 py-10 sm:py-14">
        <header
          className="mb-8 rounded-[26px] border border-border/60 p-5 text-center shadow-sm sm:p-7"
          style={{
            backgroundImage:
              "linear-gradient(150deg, var(--qa-indigo-tint) 0%, var(--qa-lavender-tint) 45%, var(--card) 100%)",
          }}
        >
          <div
            className="mb-4 inline-flex items-center gap-2 rounded-full bg-card px-3 py-1 text-xs uppercase tracking-wider"
            style={{ color: "var(--qa-indigo-ink)" }}
          >
            <Moon className="h-3.5 w-3.5" /> Sleep & Dreams
          </div>
          <h1 className="font-display text-4xl tracking-tight text-foreground md:text-5xl">
            Rest is part of healing
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-base leading-relaxed text-muted-foreground">
            A peaceful sanctuary to notice your sleep, explore your dreams, and listen
            to what your nervous system is processing through the night.
          </p>
          <p className="mx-auto mt-3 max-w-xl rounded-2xl bg-gold/5 px-4 py-3 text-xs leading-relaxed text-muted-foreground">
            <strong className="text-[var(--gold-deep)]">A gentle note:</strong> dreams are
            personal and symbolic. There is no single interpretation. This space is for
            reflection and curiosity — never diagnosis.
          </p>
        </header>

        {/* Tabs */}
        <div className="mb-8 -mx-4 flex snap-x snap-mandatory gap-2 overflow-x-auto px-4 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden sm:mx-0 sm:flex-wrap sm:justify-center sm:overflow-visible sm:px-0">
          {TABS.map((t, i) => {
            const Icon = t.icon;
            const active = tab === t.id;
            const a = accentAt(i);
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                style={{
                  backgroundColor: a.tint,
                  color: a.ink,
                  boxShadow: active ? `inset 0 0 0 2px ${a.ink}` : `inset 0 0 0 1px ${a.tint}`,
                  opacity: active ? 1 : 0.75,
                }}
                className={[
                  "inline-flex min-h-11 shrink-0 snap-start items-center gap-1.5 whitespace-nowrap rounded-full px-4 py-2 text-sm transition-all",
                  active ? "font-semibold" : "font-medium",
                ].join(" ")}
              >
                <Icon className="h-4 w-4" />
                {t.label}
              </button>
            );
          })}
        </div>

        {tab === "tracker" && <SleepTracker />}
        {tab === "dreams" && <DreamJournal />}
        {tab === "checkin" && <DailyCheckIn />}
        {tab === "patterns" && <Patterns />}
        {tab === "library" && <SleepLibrary />}
        {tab === "practices" && <GuidedPractices />}
        {tab === "sounds" && <Soundscapes />}

        <footer className="mt-12 text-center">
          <Link
            to="/dreams"
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
          >
            <Sparkles className="h-4 w-4" /> Want an AI-assisted dream reflection? Visit the
            classic Dream Journal →
          </Link>
        </footer>
      </div>
    </div>
  );
}

// ---------------- Sleep Tracker ----------------

function SleepTracker() {
  const { items: entries, commit } = useSleepStore<SleepEntry>("sleep");
  const [form, setForm] = useState<SleepEntry>(emptySleep());

  function emptySleep(): SleepEntry {
    return {
      id: uid(),
      date: todayISO(),
      bedtime: "22:30",
      wakeTime: "07:00",
      hours: 8,
      quality: 6,
      energy: 6,
      awakenings: 0,
      notes: "",
    };
  }

  function submit(e: React.FormEvent) {
    e.preventDefault();
    const next = [{ ...form, id: uid() }, ...entries].slice(0, 200);
    commit(next);
    setForm(emptySleep());
  }

  function remove(id: string) {
    commit(entries.filter((x) => x.id !== id));
  }

  return (
    <div className="grid gap-8 md:grid-cols-5">
      <form
        onSubmit={submit}
        className="md:col-span-3 rounded-2xl border border-border/60 bg-card p-6 shadow-sm"
      >
        <h2 className="mb-4 font-display text-2xl text-foreground">Log last night's sleep</h2>

        <div className="grid grid-cols-2 gap-4">
          <Field label="Date">
            <input
              type="date"
              value={form.date}
              onChange={(e) => setForm({ ...form, date: e.target.value })}
              className={inputClass}
            />
          </Field>
          <Field label="Hours slept">
            <input
              type="number"
              step="0.25"
              min={0}
              max={24}
              value={form.hours}
              onChange={(e) => setForm({ ...form, hours: parseFloat(e.target.value) || 0 })}
              className={inputClass}
            />
          </Field>
          <Field label="Bedtime">
            <input
              type="time"
              value={form.bedtime}
              onChange={(e) => setForm({ ...form, bedtime: e.target.value })}
              className={inputClass}
            />
          </Field>
          <Field label="Wake time">
            <input
              type="time"
              value={form.wakeTime}
              onChange={(e) => setForm({ ...form, wakeTime: e.target.value })}
              className={inputClass}
            />
          </Field>
          <Field label={`Sleep quality — ${form.quality}/10`}>
            <input
              type="range"
              min={1}
              max={10}
              value={form.quality}
              onChange={(e) => setForm({ ...form, quality: parseInt(e.target.value) })}
              className="w-full accent-[var(--gold)]"
            />
          </Field>
          <Field label={`Energy on waking — ${form.energy}/10`}>
            <input
              type="range"
              min={1}
              max={10}
              value={form.energy}
              onChange={(e) => setForm({ ...form, energy: parseInt(e.target.value) })}
              className="w-full accent-[var(--gold)]"
            />
          </Field>
          <Field label="Night awakenings">
            <input
              type="number"
              min={0}
              max={20}
              value={form.awakenings}
              onChange={(e) => setForm({ ...form, awakenings: parseInt(e.target.value) || 0 })}
              className={inputClass}
            />
          </Field>
        </div>

        <Field label="Sleep notes (optional)">
          <textarea
            rows={3}
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            placeholder="How did your body feel? Anything you noticed before bed?"
            className={inputClass}
          />
        </Field>

        <button type="submit" className="btn-gold mt-4 inline-flex items-center gap-2 rounded-full px-6 py-2.5 text-sm">
          <Plus className="h-4 w-4" /> Save entry
        </button>
      </form>

      <aside className="md:col-span-2 space-y-3">
        <h3 className="px-1 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
          Recent nights
        </h3>
        {entries.length === 0 && (
          <p className="rounded-2xl border border-dashed border-border bg-card/50 p-6 text-sm text-muted-foreground">
            Your sleep history will appear here as you log nights.
          </p>
        )}
        {entries.slice(0, 8).map((e) => (
          <div key={e.id} className="rounded-2xl border border-border/60 bg-card p-4 shadow-sm">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
                <CalendarDays className="h-4 w-4 text-gold" /> {e.date}
              </div>
              <button onClick={() => remove(e.id)} className="text-muted-foreground hover:text-destructive">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
            <div className="mt-2 grid grid-cols-3 gap-2 text-xs text-muted-foreground">
              <span>💤 {e.hours}h</span>
              <span>⭐ {e.quality}/10</span>
              <span>⚡ {e.energy}/10</span>
            </div>
            {e.notes && <p className="mt-2 text-xs text-foreground/80">{e.notes}</p>}
          </div>
        ))}
      </aside>
    </div>
  );
}

// ---------------- Dream Journal ----------------

function DreamJournal() {
  const { items: entries, commit } = useSleepStore<DreamEntry>("dream");
  const [form, setForm] = useState<DreamEntry>(emptyDream());
  const [openSection, setOpenSection] = useState<"dream" | "feelings" | "symbols" | "meaning">("dream");
  const [openGuide, setOpenGuide] = useState<string | null>("why");
  const [justSaved, setJustSaved] = useState<DreamEntry | null>(null);

  function emptyDream(): DreamEntry {
    return {
      id: uid(),
      date: todayISO(),
      title: "",
      description: "",
      emotions: [],
      people: "",
      locations: "",
      symbols: [],
      themes: [],
      reflection: "",
    };
  }

  function toggle<K extends "emotions" | "symbols" | "themes">(key: K, value: string) {
    const list = form[key];
    setForm({
      ...form,
      [key]: list.includes(value) ? list.filter((v) => v !== value) : [...list, value],
    });
  }

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.title.trim() && !form.description.trim()) return;
    const saved = { ...form, id: uid() };
    const next = [saved, ...entries].slice(0, 200);
    commit(next);
    setForm(emptyDream());
    setJustSaved(saved);
    if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function remove(id: string) {
    commit(entries.filter((x) => x.id !== id));
  }

  const summaries = {
    dream: form.title.trim() || (form.description.trim() ? "Dream captured" : "Write what you remember"),
    feelings: form.emotions.length ? `${form.emotions.length} selected` : "How did it feel?",
    symbols: form.symbols.length ? `${form.symbols.length} selected` : "What appeared?",
    meaning: form.reflection.trim() || form.themes.length ? "Reflection added" : "Explore gently, if you wish",
  };

  return (
    <div className="space-y-8 pb-[calc(env(safe-area-inset-bottom)+5rem)] lg:pb-0">
      {justSaved && <DreamSavedNextSteps dream={justSaved} onClose={() => setJustSaved(null)} />}
      <form onSubmit={submit} className="overflow-hidden rounded-3xl border border-gold/35 bg-card shadow-[var(--shadow-soft-lg)]">
        <div className="relative overflow-hidden border-b border-gold/25 bg-gradient-to-br from-[var(--qa-indigo-tint)] via-[var(--qa-gold-tint)] to-card px-5 py-6 text-center sm:px-8">
          <Cloud className="absolute start-5 top-5 h-8 w-8 text-[var(--qa-sky-ink)]/35" aria-hidden />
          <Stars className="absolute end-5 top-5 h-7 w-7 text-[var(--gold-deep)]/60" aria-hidden />
          <span className="mx-auto grid h-14 w-14 place-items-center rounded-full border border-gold/35 bg-card/80 text-[var(--gold-deep)] shadow-sm" aria-hidden><MoonStar className="h-7 w-7" /></span>
          <h2 className="mt-3 font-display text-2xl text-foreground">Last night’s dream</h2>
          <p className="mx-auto mt-1.5 max-w-lg text-sm leading-relaxed text-muted-foreground">
            Capture whatever remains — a feeling, image, place or fragment. It does not need to make sense.
          </p>
          <div className="mt-3 flex justify-center gap-2 text-sm text-[var(--gold-deep)]" aria-hidden>
            <Star className="h-3.5 w-3.5" fill="currentColor" />
            <CloudMoon className="h-4 w-4" />
            <Star className="h-3 w-3" fill="currentColor" />
          </div>
        </div>

        <div className="space-y-2 p-3 sm:p-5">
          <DreamSection
            id="dream"
            icon={<MoonStar className="h-5 w-5" />}
            title="Dream"
            summary={summaries.dream}
            open={openSection === "dream"}
            onToggle={() => setOpenSection(openSection === "dream" ? "feelings" : "dream")}
          >
            <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_11rem]">
              <Field label="Dream title">
                <input
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="The house with many doors…"
                  className={inputClass}
                />
              </Field>
              <Field label="Date">
                <input
                  type="date"
                  value={form.date}
                  onChange={(e) => setForm({ ...form, date: e.target.value })}
                  className={inputClass}
                />
              </Field>
            </div>
            <Field label="Dream description">
              <div className="relative mt-1.5 overflow-hidden rounded-2xl border border-gold/30 bg-[var(--qa-gold-tint)]/45 shadow-inner focus-within:ring-2 focus-within:ring-ring">
                <PenLine className="pointer-events-none absolute end-4 top-4 h-4 w-4 text-[var(--gold-deep)]/55" aria-hidden />
                <textarea
                  rows={7}
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="I remember…"
                  className="min-h-44 w-full resize-y bg-transparent px-4 py-3 pe-11 text-base leading-8 text-foreground outline-none placeholder:text-muted-foreground/65"
                  style={{ backgroundImage: "repeating-linear-gradient(transparent, transparent 31px, color-mix(in oklab, var(--gold) 18%, transparent) 32px)" }}
                />
              </div>
            </Field>
          </DreamSection>

          <DreamSection id="feelings" icon={<Heart className="h-5 w-5" />} title="Feelings" summary={summaries.feelings} open={openSection === "feelings"} onToggle={() => setOpenSection(openSection === "feelings" ? "symbols" : "feelings")}>
            <VisualChoiceGrid options={DREAM_EMOTIONS} selected={form.emotions} onToggle={(v) => toggle("emotions", v)} />
          </DreamSection>

          <DreamSection id="symbols" icon={<Sparkles className="h-5 w-5" />} title="Symbols" summary={summaries.symbols} open={openSection === "symbols"} onToggle={() => setOpenSection(openSection === "symbols" ? "meaning" : "symbols")}>
            <p className="mb-3 text-sm text-muted-foreground">Choose anything that stood out. Symbols are personal; there is no fixed meaning.</p>
            <VisualChoiceGrid options={DREAM_SYMBOLS} selected={form.symbols} onToggle={(v) => toggle("symbols", v)} />
          </DreamSection>

          <DreamSection id="meaning" icon={<CloudMoon className="h-5 w-5" />} title="Meaning & Reflection" summary={summaries.meaning} open={openSection === "meaning"} onToggle={() => setOpenSection(openSection === "meaning" ? "dream" : "meaning")}>
            <ChipGroup label="Themes I noticed" options={DREAM_THEMES} selected={form.themes} onToggle={(v) => toggle("themes", v)} />
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="People in the dream">
                <input value={form.people} onChange={(e) => setForm({ ...form, people: e.target.value })} placeholder="A friend, a stranger…" className={inputClass} />
              </Field>
              <Field label="Places in the dream">
                <input value={form.locations} onChange={(e) => setForm({ ...form, locations: e.target.value })} placeholder="A house, the sea, a forest…" className={inputClass} />
              </Field>
            </div>
            <div className="mt-4">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Gentle questions</p>
              <div className="mt-2 grid gap-2 sm:grid-cols-2">
                {DREAM_REFLECTION_PROMPTS.map((prompt) => (
                  <div key={prompt} className="flex items-start gap-2 rounded-xl border border-gold/20 bg-[var(--qa-gold-tint)]/40 px-3 py-2.5 text-xs leading-relaxed text-foreground/85">
                    <Sparkles className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[var(--gold-deep)]" aria-hidden />
                    <span>{prompt}</span>
                  </div>
                ))}
              </div>
              <p className="mt-2 text-xs text-muted-foreground">Choose only what feels useful. You do not need to answer every question.</p>
            </div>
            <Field label="Your reflection">
              <textarea rows={5} value={form.reflection} onChange={(e) => setForm({ ...form, reflection: e.target.value })} placeholder="The part that stays with me is…" className={inputClass} />
            </Field>
          </DreamSection>

          <div className="px-1 pb-1 pt-2">
            <Button type="submit" variant="gold" className="w-full rounded-full sm:w-auto sm:px-7">
              <Plus className="h-4 w-4" /> Save dream
            </Button>
            <p className="mt-2 text-center text-xs text-muted-foreground sm:text-start">Your dream is private and saved to your account.</p>
          </div>
        </div>
      </form>

      <section aria-labelledby="understanding-dreams-heading">
        <div className="mb-3 px-1">
          <p className="text-xs font-semibold uppercase tracking-wider text-[var(--gold-deep)]">Dream guide</p>
          <h3 id="understanding-dreams-heading" className="mt-1 font-display text-2xl text-foreground">Understanding your dream world</h3>
          <p className="mt-1 text-sm text-muted-foreground">Gentle education for dreaming, recall and lucid dreaming.</p>
        </div>
        <div className="space-y-2">
          {DREAM_GUIDES.map((guide) => {
            const GuideIcon = guide.icon;
            const open = openGuide === guide.id;
            return (
              <article key={guide.id} className="overflow-hidden rounded-2xl border border-border/70 bg-card">
                <Button
                  type="button"
                  variant="ghost"
                  aria-expanded={open}
                  aria-controls={`dream-guide-${guide.id}`}
                  onClick={() => setOpenGuide(open ? null : guide.id)}
                  className="grid min-h-[68px] h-auto w-full grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-3 rounded-none px-4 py-3 text-start hover:bg-gold/10"
                >
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-gold/15 text-[var(--gold-deep)]" aria-hidden>
                    <GuideIcon className="h-5 w-5" />
                  </span>
                  <span className="min-w-0 whitespace-normal">
                    <span className="block font-display text-base text-foreground">{guide.title}</span>
                    <span className="mt-0.5 block text-xs font-normal leading-relaxed text-muted-foreground">{guide.summary}</span>
                  </span>
                  <ChevronDown className={`h-4 w-4 shrink-0 text-[var(--gold-deep)] transition-transform ${open ? "rotate-180" : ""}`} aria-hidden />
                </Button>
                {open ? (
                  <div id={`dream-guide-${guide.id}`} className="border-t border-gold/20 px-5 py-4 text-sm leading-relaxed text-foreground/85">
                    {guide.content}
                  </div>
                ) : null}
              </article>
            );
          })}
        </div>
      </section>

      {entries.length > 0 && (
        <section>
          <h3 className="mb-3 px-1 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
            Dream archive
          </h3>
          <div className="grid gap-4 md:grid-cols-2">
            {entries.map((d) => (
              <article key={d.id} className="rounded-2xl border border-border/60 bg-card p-5 shadow-sm">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="text-xs text-muted-foreground">{d.date}</div>
                    <h4 className="font-display text-lg text-foreground">{d.title || "Untitled dream"}</h4>
                  </div>
                  <div className="flex shrink-0 items-center gap-1">
                    <FavoriteButton
                      storageKey="rt:fav-dream-entries"
                      id={d.id}
                      label={d.title || "Untitled dream"}
                      sub="Dream journal entry"
                      to="/sleep-dreams"
                      size="sm"
                    />
                    <Button type="button" variant="ghost" size="icon" aria-label={`Delete ${d.title || "untitled dream"}`} onClick={() => remove(d.id)} className="h-11 w-11 text-muted-foreground hover:text-destructive">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                {d.description && (
                  <p className="mt-2 line-clamp-4 text-sm leading-relaxed text-foreground/85">{d.description}</p>
                )}
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {[...d.emotions, ...d.themes, ...d.symbols].slice(0, 8).map((tag) => (
                    <span key={tag} className="rounded-full bg-gold/10 px-2 py-0.5 text-[11px] text-[var(--gold-deep)]">
                      {tag}
                    </span>
                  ))}
                </div>
                {d.reflection && (
                  <p className="mt-3 border-t border-border/60 pt-3 text-xs italic text-muted-foreground">
                    “{d.reflection}”
                  </p>
                )}
                <div className="mt-4 grid gap-2 sm:grid-cols-2">
                  <Button asChild variant="gold" className="w-full rounded-full">
                    <Link to="/dream-reflections" search={{ dream: d.id }}>
                      <PenLine className="h-4 w-4" /> Write a reflection
                    </Link>
                  </Button>
                  <Button asChild variant="goldOutline" className="w-full rounded-full">
                    <Link to="/dream-reading" search={{ dream: d.id }}>
                      <WandSparkles className="h-4 w-4" /> Gentle dream reading
                    </Link>
                  </Button>
                </div>
              </article>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function DreamSection({
  id,
  icon,
  title,
  summary,
  open,
  onToggle,
  children,
}: {
  id: string;
  icon: ReactNode;
  title: string;
  summary: string;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return (
    <section className={`overflow-hidden rounded-2xl border transition-colors ${open ? "border-gold/55 bg-background/70" : "border-border/65 bg-background/35"}`}>
      <Button
        type="button"
        variant="ghost"
        aria-expanded={open}
        aria-controls={`dream-section-${id}`}
        onClick={onToggle}
        className="grid min-h-[60px] w-full grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-3 rounded-none px-3.5 py-2.5 text-start hover:bg-gold/10"
      >
        <span className={`grid h-9 w-9 shrink-0 place-items-center rounded-full text-[var(--gold-deep)] ${open ? "bg-gold/25 ring-1 ring-gold" : "bg-muted"}`} aria-hidden>{icon}</span>
        <span className="min-w-0">
          <span className="block font-display text-base text-foreground">{title}</span>
          <span className="block truncate text-xs font-normal text-muted-foreground">{summary}</span>
        </span>
        <ChevronDown className={`h-4 w-4 shrink-0 text-[var(--gold-deep)] transition-transform ${open ? "rotate-180" : ""}`} aria-hidden />
      </Button>
      {open ? <div id={`dream-section-${id}`} className="border-t border-gold/20 px-4 pb-4 pt-2 sm:px-5">{children}</div> : null}
    </section>
  );
}

function VisualChoiceGrid({
  options,
  selected,
  onToggle,
}: {
  options: ReadonlyArray<DreamVisualOption>;
  selected: string[];
  onToggle: (value: string) => void;
}) {
  return (
    <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-6">
      {options.map((option) => {
        const active = selected.includes(option.label);
        const ChoiceIcon = option.icon;
        return (
          <Button
            key={option.label}
            type="button"
            variant="outline"
            aria-pressed={active}
            onClick={() => onToggle(option.label)}
            className={`h-auto min-h-[66px] min-w-0 flex-col gap-1 rounded-xl px-1.5 py-2 text-[11px] leading-tight whitespace-normal ${active ? "border-gold bg-gold/25 text-foreground ring-2 ring-gold ring-offset-1 ring-offset-background" : "border-border/70 bg-card text-muted-foreground"}`}
          >
            <ChoiceIcon className="h-5 w-5 shrink-0 text-[var(--gold-deep)]" aria-hidden />
            <span className="max-w-full break-words">{option.label}</span>
          </Button>
        );
      })}
    </div>
  );
}

// ---------------- Daily Check-In ----------------

function DailyCheckIn() {
  const { items: entries, commit } = useSleepStore<CheckIn>("checkin");
  const [form, setForm] = useState<CheckIn>({
    id: uid(),
    date: todayISO(),
    rested: 5,
    connected: 5,
    rememberedDream: false,
    bodyNeeds: "",
  });

  function submit(e: React.FormEvent) {
    e.preventDefault();
    const next = [{ ...form, id: uid() }, ...entries].slice(0, 200);
    commit(next);
    setForm({ ...form, id: uid(), bodyNeeds: "" });
  }

  return (
    <div className="grid gap-8 md:grid-cols-5">
      <form
        onSubmit={submit}
        className="md:col-span-3 rounded-2xl border border-border/60 bg-card p-6 shadow-sm"
      >
        <h2 className="mb-2 font-display text-2xl text-foreground">Sleep & recovery check-in</h2>
        <p className="mb-4 text-sm text-muted-foreground">A gentle daily pulse — no right answers.</p>

        <Field label={`How rested do you feel today? — ${form.rested}/10`}>
          <input
            type="range"
            min={1}
            max={10}
            value={form.rested}
            onChange={(e) => setForm({ ...form, rested: parseInt(e.target.value) })}
            className="w-full accent-[var(--gold)]"
          />
        </Field>
        <Field label={`How connected do you feel to yourself? — ${form.connected}/10`}>
          <input
            type="range"
            min={1}
            max={10}
            value={form.connected}
            onChange={(e) => setForm({ ...form, connected: parseInt(e.target.value) })}
            className="w-full accent-[var(--gold)]"
          />
        </Field>

        <label className="mt-2 flex items-center gap-2 text-sm text-foreground">
          <input
            type="checkbox"
            checked={form.rememberedDream}
            onChange={(e) => setForm({ ...form, rememberedDream: e.target.checked })}
            className="h-4 w-4 accent-[var(--gold)]"
          />
          I remember a dream from last night
        </label>

        <Field label="What does your body need today?">
          <textarea
            rows={3}
            value={form.bodyNeeds}
            onChange={(e) => setForm({ ...form, bodyNeeds: e.target.value })}
            placeholder="Slowness, warmth, quiet, a long walk…"
            className={inputClass}
          />
        </Field>

        <button type="submit" className="btn-gold mt-4 inline-flex items-center gap-2 rounded-full px-6 py-2.5 text-sm">
          <Plus className="h-4 w-4" /> Save check-in
        </button>
      </form>

      <aside className="md:col-span-2 space-y-3">
        <h3 className="px-1 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
          Recent check-ins
        </h3>
        {entries.length === 0 && (
          <p className="rounded-2xl border border-dashed border-border bg-card/50 p-6 text-sm text-muted-foreground">
            Your daily reflections will appear here.
          </p>
        )}
        {entries.slice(0, 8).map((c) => (
          <div key={c.id} className="rounded-2xl border border-border/60 bg-card p-4 shadow-sm">
            <div className="flex items-center justify-between text-sm font-semibold text-foreground">
              <span className="flex items-center gap-2">
                <Sun className="h-4 w-4 text-gold" /> {c.date}
              </span>
              {c.rememberedDream && <Star className="h-4 w-4 text-[var(--gold-deep)]" />}
            </div>
            <div className="mt-2 text-xs text-muted-foreground">
              Rested {c.rested}/10 · Connected {c.connected}/10
            </div>
            {c.bodyNeeds && <p className="mt-2 text-xs text-foreground/80">{c.bodyNeeds}</p>}
          </div>
        ))}
      </aside>
    </div>
  );
}

// ---------------- Patterns ----------------

function Patterns() {
  const { items: sleep } = useSleepStore<SleepEntry>("sleep");
  const { items: dreams } = useSleepStore<DreamEntry>("dream");

  const avg = useMemo(() => {
    if (sleep.length === 0) return null;
    const sum = sleep.reduce(
      (acc, s) => ({
        hours: acc.hours + s.hours,
        quality: acc.quality + s.quality,
        energy: acc.energy + s.energy,
        awakenings: acc.awakenings + s.awakenings,
      }),
      { hours: 0, quality: 0, energy: 0, awakenings: 0 },
    );
    const n = sleep.length;
    return {
      hours: (sum.hours / n).toFixed(1),
      quality: (sum.quality / n).toFixed(1),
      energy: (sum.energy / n).toFixed(1),
      awakenings: (sum.awakenings / n).toFixed(1),
    };
  }, [sleep]);

  const tally = (arr: string[]) => {
    const map = new Map<string, number>();
    arr.forEach((v) => map.set(v, (map.get(v) || 0) + 1));
    return [...map.entries()].sort((a, b) => b[1] - a[1]).slice(0, 8);
  };

  const topEmotions = tally(dreams.flatMap((d) => d.emotions));
  const topSymbols = tally(dreams.flatMap((d) => d.symbols));
  const topThemes = tally(dreams.flatMap((d) => d.themes));

  return (
    <div className="space-y-6">
      <section className="rounded-2xl border border-border/60 bg-card p-6 shadow-sm">
        <h2 className="mb-4 font-display text-2xl text-foreground">Sleep trends</h2>
        {avg ? (
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
            <Stat label="Avg hours" value={avg.hours} />
            <Stat label="Avg quality" value={`${avg.quality}/10`} />
            <Stat label="Avg energy" value={`${avg.energy}/10`} />
            <Stat label="Avg awakenings" value={avg.awakenings} />
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">Log a few nights to see trends.</p>
        )}
      </section>

      <section className="rounded-2xl border border-border/60 bg-card p-6 shadow-sm">
        <h2 className="mb-4 font-display text-2xl text-foreground">Dream patterns</h2>
        {dreams.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Save a few dreams to discover recurring symbols, emotions, and themes.
          </p>
        ) : (
          <div className="grid gap-6 md:grid-cols-3">
            <PatternList title="Recurring emotions" items={topEmotions} />
            <PatternList title="Recurring symbols" items={topSymbols} />
            <PatternList title="Recurring themes" items={topThemes} />
          </div>
        )}
      </section>

      <section className="rounded-2xl border border-gold/30 bg-gold/5 p-6">
        <h2 className="mb-3 font-display text-xl text-foreground">Monthly reflection</h2>
        <ul className="space-y-2 text-sm text-foreground/85">
          <li>· What themes appeared in my dreams this month?</li>
          <li>· How has my sleep been affecting my wellbeing?</li>
          <li>· What has my nervous system been communicating?</li>
          <li>· What patterns am I noticing — without needing to interpret them?</li>
        </ul>
      </section>
    </div>
  );
}

// ---------------- Sleep Library ----------------

const NATURAL_REMEDIES = [
  {
    emoji: "💊",
    title: "Magnesium & Sleep",
    body:
      "Magnesium plays a role in relaxing muscles and calming the nervous system, which is why many people use it as a gentle bedtime support. Common forms include magnesium glycinate (often chosen for calm) and citrate (often chosen for digestion). Always check with a healthcare professional before adding supplements, especially if you take medication.",
  },
  {
    emoji: "🌼",
    title: "Herbal Teas",
    body:
      "Warm, caffeine-free teas can become a soothing pre-sleep ritual. Chamomile is traditionally used for calm; lemon balm and passionflower are gentle on a busy mind; lavender tea pairs well with quiet evenings. Sip slowly, an hour or so before bed.",
  },
  {
    emoji: "🕯️",
    title: "Aromatherapy",
    body:
      "Calming scents like lavender, cedarwood, and bergamot can signal the body that it is safe to slow down. A few drops in a diffuser, on a pillow corner, or on the wrists can help shape a restful environment.",
  },
  {
    emoji: "🛁",
    title: "Warm Bath or Shower",
    body:
      "A warm bath or shower relaxes muscles and gently lowers core body temperature afterwards — a natural cue for sleep. It also creates a clear transition between the day and rest, making it easier for the nervous system to soften.",
  },
  {
    emoji: "🥣",
    title: "Gentle Evening Nutrition",
    body:
      "Limiting caffeine after early afternoon, going easy on alcohol (which disrupts deep sleep), staying hydrated, and keeping blood sugar steady with a light, balanced evening meal can all support more restful nights.",
  },
];

const CANT_SLEEP_STEPS = [
  {
    emoji: "🤍",
    title: "Don't panic",
    body: "Not sleeping immediately is not dangerous. Stress about sleep often makes sleep harder. Let this moment be okay.",
  },
  {
    emoji: "🚪",
    title: "Leave the bed if needed",
    body: "If you've been awake for a while, get up. Read something calming, listen to gentle audio, or breathe slowly. Return to bed when sleepy.",
  },
  {
    emoji: "🌿",
    title: "Ground in the present (5-4-3-2-1)",
    body: "Name 5 things you can see, 4 you can feel, 3 you can hear, 2 you can smell, 1 you can taste. This brings your nervous system back to now.",
  },
  {
    emoji: "📝",
    title: "Calm racing thoughts",
    body: "Try a brain dump on paper, a quick worry list, slow breathing, or a guided meditation. Let the thoughts land somewhere outside of you.",
  },
];

const BEDTIME_TOOLKIT = [
  { emoji: "🌙", title: "2-Minute Calm", blurb: "A short grounding pause.", to: "/grounding" },
  { emoji: "💨", title: "3-Minute Breathing", blurb: "Slow, simple guided breath.", to: "/breathing" },
  { emoji: "🧘", title: "5-Minute Body Scan", blurb: "Gently release tension.", to: "/somatic" },
  { emoji: "🎧", title: "Sleep Reset Audio", blurb: "Quick calming soundscape.", to: "/sounds" },
] as const;

const AFFIRMATIONS = [
  "I am safe in this moment.",
  "My body knows how to rest.",
  "I do not need to force sleep.",
  "Rest is enough.",
  "It is okay to let go of today.",
  "I am allowed to soften.",
];

const TRAUMA_TIPS = [
  "Hypervigilance at night",
  "Nightmares or vivid dreams",
  "Frequent waking",
  "Fear of sleep or of darkness",
  "Racing thoughts as the room quiets",
];

const EMERGENCY_CALM = [
  { emoji: "🌙", title: "Grounding Exercise", blurb: "5-4-3-2-1 senses.", to: "/grounding" },
  { emoji: "💨", title: "Breathing Exercise", blurb: "Long, slow exhales.", to: "/breathing" },
  { emoji: "🤍", title: "Self-Compassion", blurb: "A reminder you're not alone.", to: "/affirmations" },
  { emoji: "🎧", title: "Calming Audio", blurb: "Soft soundscape to lean into.", to: "/sounds" },
  { emoji: "📖", title: "Journal Prompt", blurb: "Let the mind set something down.", to: "/journal" },
] as const;

function SleepLibrary() {
  return (
    <div className="space-y-10">
      {/* Original deep-dive articles */}
      <section>
        <SectionHeading emoji="📚" title="Understanding Sleep & Trauma" />
        <div className="grid gap-4 md:grid-cols-2">
          {SLEEP_LIBRARY.map((item) => {
            const Icon = item.icon;
            return (
              <article
                key={item.title}
                className="group rounded-2xl border border-[#dde7f3] bg-gradient-to-br from-[#f4f8fd] to-white p-6 shadow-sm transition-all hover:shadow-md dark:border-border dark:from-card dark:to-background"
              >
                <span className="mb-3 inline-flex h-10 w-10 items-center justify-center rounded-full bg-[#e6eef9] text-[#5a6f97] dark:bg-accent dark:text-accent-foreground">
                  <Icon className="h-5 w-5" />
                </span>
                <h3 className="font-display text-lg text-[#2c3a55] dark:text-foreground">{item.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-[#4f5a72] dark:text-muted-foreground">{item.blurb}</p>
              </article>
            );
          })}
        </div>
      </section>

      {/* Natural sleep support */}
      <section>
        <SectionHeading emoji="🌿" title="Natural Sleep Support & Gentle Remedies" />
        <p className="mb-4 text-sm text-[#4f5a72] dark:text-muted-foreground">
          Education and self-care ideas to support your nervous system tonight. These are
          gentle suggestions — not medical advice. Always check in with a healthcare
          professional when appropriate.
        </p>
        <div className="grid gap-4 md:grid-cols-2">
          {NATURAL_REMEDIES.map((r) => (
            <article
              key={r.title}
              className="rounded-2xl border border-[#d8e6d8] bg-gradient-to-br from-[#f1f7ee] to-white p-6 shadow-sm dark:border-border dark:from-card dark:to-background"
            >
              <div className="mb-2 flex items-center gap-2 text-2xl">{r.emoji}</div>
              <h3 className="font-display text-lg text-[#2c3a55] dark:text-foreground">{r.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-[#4f5a72] dark:text-muted-foreground">{r.body}</p>
            </article>
          ))}
        </div>
        <ReminderBox>
          🤍 These remedies are companions, not cures. The most powerful sleep support is
          consistent gentleness toward yourself.
        </ReminderBox>
      </section>

      {/* What to do when you can't sleep */}
      <section>
        <SectionHeading emoji="🌙" title="What To Do When You Can't Sleep" />
        <p className="mb-4 text-sm text-[#4f5a72] dark:text-muted-foreground">
          Instead of trying harder to sleep, try softening. These steps help when the night
          feels long.
        </p>
        <div className="grid gap-4 md:grid-cols-2">
          {CANT_SLEEP_STEPS.map((step) => (
            <article
              key={step.title}
              className="rounded-2xl border border-[#dce4f3] bg-gradient-to-br from-[#eef3fb] to-white p-6 shadow-sm dark:border-border dark:from-card dark:to-background"
            >
              <div className="mb-2 text-2xl">{step.emoji}</div>
              <h3 className="font-display text-base text-[#2c3a55] dark:text-foreground">{step.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-[#4f5a72] dark:text-muted-foreground">{step.body}</p>
            </article>
          ))}
        </div>
      </section>

      {/* Bedtime toolkit */}
      <section>
        <SectionHeading emoji="✨" title="Bedtime Toolkit — Quick Actions" />
        <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-4">
          {BEDTIME_TOOLKIT.map((t) => (
            <Link
              key={t.title}
              to={t.to}
              className="group rounded-2xl border border-[#e3d8ee] bg-gradient-to-br from-[#f4eefa] to-white p-5 text-start shadow-sm transition-all hover:shadow-md hover:-translate-y-0.5 dark:border-border dark:from-card dark:to-background"
            >
              <div className="mb-2 text-2xl">{t.emoji}</div>
              <h3 className="font-display text-sm text-[#2c3a55] dark:text-foreground">{t.title}</h3>
              <p className="mt-1 text-xs text-[#4f5a72] dark:text-muted-foreground">{t.blurb}</p>
            </Link>
          ))}
        </div>
      </section>

      {/* Affirmations */}
      <section>
        <SectionHeading emoji="🤍" title="Gentle Affirmations" />
        <div className="grid gap-3 sm:grid-cols-2">
          {AFFIRMATIONS.map((a) => (
            <div
              key={a}
              className="rounded-2xl border border-[#e8e0f1] bg-gradient-to-br from-white to-[#f5f0fa] px-5 py-4 text-sm leading-relaxed text-[#2c3a55] shadow-sm dark:border-border dark:from-card dark:to-background dark:text-foreground"
            >
              🤍 {a}
            </div>
          ))}
        </div>
      </section>

      {/* Trauma-informed sleep tips */}
      <section>
        <SectionHeading emoji="🌸" title="Trauma-Informed Sleep Tips" />
        <article className="rounded-2xl border border-[#f0dce4] bg-gradient-to-br from-[#fbf1f5] to-white p-6 shadow-sm dark:border-border dark:from-card dark:to-background">
          <p className="text-sm leading-relaxed text-[#4f5a72] dark:text-muted-foreground">
            Many trauma survivors experience disrupted sleep. These are common nervous
            system responses, not signs of weakness or failure:
          </p>
          <ul className="mt-3 grid gap-2 sm:grid-cols-2">
            {TRAUMA_TIPS.map((t) => (
              <li
                key={t}
                className="flex items-start gap-2 rounded-xl bg-white/70 px-3 py-2 text-sm text-[#2c3a55] dark:bg-background/70 dark:text-foreground"
              >
                <span className="text-[#b48ba6]">•</span> {t}
              </li>
            ))}
          </ul>
          <ReminderBox>
            🤍 Your body is doing its best to keep you safe. Healing rest takes time, and
            small, consistent kindnesses matter more than perfection.
          </ReminderBox>
        </article>
      </section>

      {/* Emergency calm */}
      <section>
        <SectionHeading emoji="🚨" title="Emergency Calm" />
        <p className="mb-4 text-sm text-[#4f5a72] dark:text-muted-foreground">
          For nights when everything feels too loud inside. Reach for any of these — there
          is no right order.
        </p>
        <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-5">
          {EMERGENCY_CALM.map((e) => (
            <Link
              key={e.title}
              to={e.to}
              className="rounded-2xl border border-[#dde7f3] bg-gradient-to-br from-[#eaf1fa] to-white p-4 text-start shadow-sm transition-all hover:shadow-md dark:border-border dark:from-card dark:to-background"
            >
              <div className="mb-2 text-2xl">{e.emoji}</div>
              <h3 className="font-display text-sm text-[#2c3a55] dark:text-foreground">{e.title}</h3>
              <p className="mt-1 text-xs text-[#4f5a72] dark:text-muted-foreground">{e.blurb}</p>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}

function SectionHeading({ emoji, title }: { emoji: string; title: string }) {
  return (
    <div className="mb-4 flex items-center gap-2">
      <span className="text-xl">{emoji}</span>
      <h2 className="font-display text-2xl text-[#2c3a55] dark:text-foreground">{title}</h2>
    </div>
  );
}

function ReminderBox({ children }: { children: React.ReactNode }) {
  return (
    <div className="mt-4 rounded-2xl border border-[#e3d8ee] bg-white/70 px-4 py-3 text-sm leading-relaxed text-[#4f5a72] backdrop-blur dark:border-border dark:bg-card dark:text-muted-foreground">
      {children}
    </div>
  );
}

// ---------------- Guided Practices ----------------

function GuidedPractices() {
  const [active, setActive] = useState<Practice | null>(null);

  return (
    <>
      <div className="grid gap-4 md:grid-cols-2">
        {GUIDED_PRACTICES.map((p) => {
          const Icon = p.icon;
          return (
            <article
              key={p.title}
              className="flex items-start gap-4 rounded-2xl border border-border/60 bg-card p-5 shadow-sm"
            >
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-gold/10 text-gold">
                <Icon className="h-5 w-5" />
              </span>
              <div className="flex-1">
                <div className="flex items-center justify-between gap-2">
                  <h3 className="font-display text-lg text-foreground">{p.title}</h3>
                  <span className="rounded-full bg-secondary px-2.5 py-0.5 text-xs text-secondary-foreground">
                    {p.duration}
                  </span>
                </div>
                <p className="mt-1 text-sm text-muted-foreground">{p.blurb}</p>
                <button
                  type="button"
                  onClick={() => setActive(p)}
                  className="btn-gold mt-4 inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm"
                >
                  <Play className="h-4 w-4" /> Begin
                </button>
              </div>
            </article>
          );
        })}
      </div>
      {active && <PracticePlayer practice={active} onClose={() => setActive(null)} />}
    </>
  );
}

function PracticePlayer({ practice, onClose }: { practice: Practice; onClose: () => void }) {
  const total = useMemo(
    () => practice.phases.reduce((s, p) => s + p.seconds, 0),
    [practice],
  );
  const [elapsed, setElapsed] = useState(0);
  const [running, setRunning] = useState(false);

  useEffect(() => {
    if (!running) return;
    const id = window.setInterval(() => {
      setElapsed((e) => {
        const next = e + 1;
        if (next >= total) {
          setRunning(false);
          return total;
        }
        return next;
      });
    }, 1000);
    return () => window.clearInterval(id);
  }, [running, total]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const { phase, phaseElapsed, phaseIndex } = useMemo(() => {
    let remaining = elapsed;
    for (let i = 0; i < practice.phases.length; i++) {
      const p = practice.phases[i];
      if (remaining < p.seconds) {
        return { phase: p, phaseElapsed: remaining, phaseIndex: i };
      }
      remaining -= p.seconds;
    }
    const last = practice.phases[practice.phases.length - 1];
    return { phase: last, phaseElapsed: last.seconds, phaseIndex: practice.phases.length - 1 };
  }, [elapsed, practice]);

  const done = elapsed >= total;
  const progress = (elapsed / total) * 100;
  const phaseProgress = (phaseElapsed / phase.seconds) * 100;
  const mmss = (s: number) =>
    `${String(Math.floor(s / 60))}:${String(s % 60).padStart(2, "0")}`;

  const reset = () => {
    setRunning(false);
    setElapsed(0);
  };

  const Icon = practice.icon;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={practice.title}
        className="relative w-full max-w-lg rounded-3xl border border-gold/30 bg-card p-6 shadow-2xl sm:p-8"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close practice"
          className="absolute end-4 top-4 rounded-full p-2 text-muted-foreground transition hover:bg-secondary hover:text-foreground"
        >
          <X className="h-5 w-5" />
        </button>

        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-full bg-gold/15 text-gold">
            <Icon className="h-5 w-5" />
          </span>
          <div>
            <h3 className="font-display text-xl text-foreground">{practice.title}</h3>
            <p className="text-xs text-muted-foreground">{practice.duration} · {practice.phases.length} steps</p>
          </div>
        </div>

        {elapsed === 0 && !running && (
          <p className="mt-4 text-sm leading-relaxed text-muted-foreground">{practice.intro}</p>
        )}

        <div className="mt-6 rounded-2xl border border-border/40 bg-background/60 p-6 text-center">
          <p className="min-h-[3.5rem] font-display text-lg leading-relaxed text-foreground">
            {done ? "You've completed the practice. Rest as long as you like." : phase.label}
          </p>
          <div className="mt-5 flex items-center justify-center gap-2 text-xs text-muted-foreground">
            <span>Step {phaseIndex + 1} of {practice.phases.length}</span>
            <span>·</span>
            <span>{mmss(elapsed)} / {mmss(total)}</span>
          </div>
          <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-secondary">
            <div
              className="h-full rounded-full bg-gold transition-[width] duration-1000 ease-linear"
              style={{ width: `${done ? 100 : phaseProgress}%` }}
            />
          </div>
          <div className="mt-2 h-1 w-full overflow-hidden rounded-full bg-secondary/60">
            <div
              className="h-full rounded-full bg-[var(--gold-deep)] transition-[width] duration-1000 ease-linear"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
          {!done ? (
            <button
              type="button"
              onClick={() => setRunning((r) => !r)}
              className="btn-gold inline-flex items-center gap-2 rounded-full px-6 py-2.5 text-sm"
            >
              {running ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
              {running ? "Pause" : elapsed === 0 ? "Begin" : "Resume"}
            </button>
          ) : (
            <button
              type="button"
              onClick={reset}
              className="btn-gold inline-flex items-center gap-2 rounded-full px-6 py-2.5 text-sm"
            >
              <RotateCcw className="h-4 w-4" /> Start again
            </button>
          )}
          {elapsed > 0 && !done && (
            <button
              type="button"
              onClick={reset}
              className="inline-flex min-h-10 sm:min-h-0 items-center gap-1.5 rounded-full border border-gold/25 px-4 py-2 text-xs font-semibold text-foreground/80 transition hover:bg-gold/10"
            >
              <RotateCcw className="h-3.5 w-3.5" /> Reset
            </button>
          )}
        </div>

        <p className="mt-5 text-center text-xs italic text-muted-foreground">
          Move at your own pace. You can pause or close anytime.
        </p>
      </div>
    </div>
  );
}


// ---------------- Soundscapes ----------------

function Soundscapes() {
  return (
    <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-3">
      {SOUNDSCAPES.map((s) => (
        <article
          key={s.title}
          className="flex items-center gap-3 rounded-2xl border border-border/60 bg-gradient-to-br from-card to-gold/5 p-5 shadow-sm"
        >
          <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-gold/15 text-gold">
            <Waves className="h-5 w-5" />
          </span>
          <div>
            <h3 className="font-display text-base text-foreground">{s.title}</h3>
            <p className="text-xs text-muted-foreground">{s.duration}</p>
          </div>
        </article>
      ))}
    </div>
  );
}

// ---------------- Shared atoms ----------------

const inputClass =
  "w-full resize-y rounded-xl border border-input bg-background p-2.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring";

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="mt-3 block">
      <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </span>
      {children}
    </label>
  );
}

function ChipGroup({
  label,
  options,
  selected,
  onToggle,
}: {
  label: string;
  options: string[];
  selected: string[];
  onToggle: (v: string) => void;
}) {
  return (
    <div className="mt-4">
      <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="flex flex-wrap gap-2">
        {options.map((opt) => {
          const active = selected.includes(opt);
          return (
            <button
              type="button"
              key={opt}
              onClick={() => onToggle(opt)}
              className={[
                "rounded-full border px-3 py-1 text-xs font-medium transition-all",
                active
                  ? "border-transparent bg-foreground text-background"
                  : "border-border bg-background text-muted-foreground hover:text-foreground",
              ].join(" ")}
            >
              {opt}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-xl border border-border/60 bg-background p-4 text-center">
      <div className="font-display text-2xl text-foreground">{value}</div>
      <div className="mt-1 text-xs text-muted-foreground">{label}</div>
    </div>
  );
}

function PatternList({ title, items }: { title: string; items: [string, number][] }) {
  return (
    <div>
      <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{title}</div>
      {items.length === 0 ? (
        <p className="text-sm text-muted-foreground">—</p>
      ) : (
        <ul className="space-y-1.5">
          {items.map(([name, count]) => (
            <li key={name} className="flex items-center justify-between rounded-lg bg-background px-3 py-1.5 text-sm">
              <span className="text-foreground">{name}</span>
              <span className="rounded-full bg-gold/10 px-2 text-xs text-[var(--gold-deep)]">{count}×</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function DreamSavedNextSteps({ dream, onClose }: { dream: DreamEntry; onClose: () => void }) {
  const heavy = dream.emotions.some((e) => /fear|scared|anx|sad|angry|shame|panic|lost|confus/i.test(e));
  const tips = heavy
    ? [
        "Place a hand on your chest and name three things you can see right now.",
        "Remind yourself: this was a dream, and you are here, now, safe enough in this moment.",
        "Go slowly — you don't need to understand the dream today.",
      ]
    : [
        "Notice which image or feeling stays with you most — that's often the thread worth following.",
        "Write one word that sums up the dream's mood before the day carries it away.",
        "Be curious rather than certain: dreams speak in symbols, not facts.",
      ];
  const next: Array<{ to: "/dream-reflections" | "/dream-reading" | "/grounding" | "/breathing"; label: string; desc: string; icon: ReactNode }> = [
    { to: "/dream-reflections", label: "Write a reflection", desc: "Explore it in your own words", icon: <PenLine className="h-5 w-5" /> },
    { to: "/dream-reading", label: "Gentle dream reading", desc: "Optional, private reflection", icon: <Sparkles className="h-5 w-5" /> },
    { to: "/grounding", label: "3-minute grounding", desc: "Settle your body after the night", icon: <Star className="h-5 w-5" /> },
    { to: "/breathing", label: "Calming breath", desc: "A slow breathing exercise", icon: <MoonStar className="h-5 w-5" /> },
  ];
  return (
    <section aria-live="polite" className="animate-fade-in rounded-3xl border border-gold/35 bg-gradient-to-br from-[var(--qa-lavender-tint)] via-card to-[var(--qa-gold-tint)] p-5 shadow-[var(--shadow-soft-lg)] sm:p-6">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="font-display text-xl text-foreground">Dream saved 🌙</h2>
          <p className="mt-1 text-sm text-muted-foreground">“{dream.title.trim() || "Untitled dream"}” is kept safely in your archive.</p>
        </div>
        <button type="button" onClick={onClose} className="shrink-0 rounded-full px-3 py-1 text-sm text-muted-foreground hover:bg-muted">Close</button>
      </div>
      <h3 className="mt-5 text-sm font-semibold uppercase tracking-wide text-[var(--gold-deep)]">Gentle tips</h3>
      <ul className="mt-2 space-y-2">
        {tips.map((t) => (
          <li key={t} className="flex gap-2 text-sm leading-relaxed text-foreground/85">
            <Star className="mt-1 h-3 w-3 shrink-0 text-[var(--gold-deep)]" fill="currentColor" aria-hidden />
            <span>{t}</span>
          </li>
        ))}
      </ul>
      <h3 className="mt-5 text-sm font-semibold uppercase tracking-wide text-[var(--gold-deep)]">What you could do next</h3>
      <div className="mt-2 grid gap-2 sm:grid-cols-2">
        {next.map((n) => (
          <Link key={n.to} to={n.to} className="flex min-h-[56px] items-center gap-3 rounded-2xl border border-gold/25 bg-card/80 px-4 py-3 transition hover:border-gold/60">
            <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-[var(--qa-gold-tint)] text-[var(--gold-deep)]" aria-hidden>{n.icon}</span>
            <span className="min-w-0">
              <span className="block text-sm font-medium text-foreground">{n.label}</span>
              <span className="block text-xs text-muted-foreground">{n.desc}</span>
            </span>
          </Link>
        ))}
      </div>
    </section>
  );
}
